<template>
  <div class="h-full flex flex-col relative">
    <div class="z-40 flex h-full w-full absolute left-0 top-0 justify-center items-center transition-opacity" v-if="loading">
      <div class="absolute left-0 top-0 h-full w-full bg-gray-600 opacity-40"></div>
      <div class="text-[2.5rem]">Loading...</div>
    </div>
    
    <!-- MOBILE -->
    <div class="sm:hidden overflow-hidden relative h-full">

      <div class="absolute top-0 left-0 h-full w-full z-0 flex flex-col">
        <!-- Mobile View: Filter Bar -->
        <div @click="toggleFilter" class="z-[100] text-center basis-[35px] bg-gray-100 flex items-center justify-center">{{ filterHidden ? 'Show Filter' : 'Hide Filter' }}</div>
        
        <!-- Shade that appears when filter is shown -->
        <div @click="toggleShade" ref="shade" class="absolute inset-0 bg-gray-600 bg-opacity-75"></div>

        <div v-if="!hideNumResults" ref="recordCount" class="absolute bottom-0 right-0 p-2"> {{ peopleList.length != 0 ? `${peopleList[0].full_record_count} results` : "No results :-(" }} </div>

        <div ref="mobileFilter" class="absolute top-[35px] left-0 flex flew-row flex-wrap border-b border-gray-200 gap-x-2 max-h-full pb-4 transition-max-h duration-100 ease-in-out overflow-hidden px-4 z-10 bg-gray-100">
          <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
            <label for="name" class="block text-sm font-medium text-gray-700">Name</label>
            <div class="mt-1">
              <input v-model="filterData.name" type="name" name="name" id="name" class="px-3 py-2 shadow-sm focus:ring-indigo-500 border focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="John Smith">
            </div>
          </div>
          <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
            <label for="country" class="block text-sm font-medium text-gray-700">Country</label>
            <select v-model="filterData.country_id" id="country" name="country" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
              <option></option>
              <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.name }}</option>
            </select>
          </div>
          <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
            <label for="training-center-group" class="block text-sm font-medium text-gray-700">Training Center Group</label>
            <select v-model="filterData.training_center_group_id" id="training-center-group" name="training-center-group" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
              <option></option>
              <option v-for="tcg in trainingCenterGroups" :key="tcg.id" :value="tcg.id">{{ tcg.name }}</option>
            </select>
          </div>
          <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
            <label for="person-role" class="block text-sm font-medium text-gray-700">Role</label>
            <select v-model="filterData.person_role" id="person-role" name="person-role" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
              <option></option>
              <option v-for="r in filterOptions.validPersonRoles" :key="r[0]" :value="r[0]">{{ r[1] }}</option>
            </select>
          </div>
          <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
            <label for="person-role-status" class="block text-sm font-medium text-gray-700">Latest Role Status</label>
            <select v-model="filterData.person_role_status" id="person-role-status" name="person-role-status" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
              <option></option>
              <option v-for="r in filterOptions.statusStrings" :key="r[0]" :value="r[0]">{{ r[1] }}</option>
            </select>
          </div>
          <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
            <label for="project" class="block text-sm font-medium text-gray-700">Project</label>
            <select v-model="filterData.project_id" id="project" name="project" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
              <option></option>
              <option v-for="project in projects" :key="project.id" :value="project.id">{{ project.name }}</option>
            </select>
          </div>
        </div>

        <!-- Mobile View: Person Selector -->
        <div class="flex-1 px-4 py-2 overflow-auto">
          <div v-if="noFilter" class="text-base text-center text-gray-500 mt-5">No filter provided.</div>
          <div v-if="!noFilter && peopleList">
            <person-select
              :person="person"
              :selectedPerson="selectedPerson"
              @selected="selectPerson"
              v-for="person in peopleList"
              :key="person.person_id"
            ></person-select>
            <InfiniteLoading :identifier="infiniteKey" @infinite="queryPeople"/>
          </div>
        </div>
      </div>
      
      <!-- Mobile View: Content Pane w/ Back Button -->
      <transition name="slide" type="transition">
        <div v-if="showContentPane" :key="'content-pane'" class="fixed inset-0 z-40 flex flex-col">
          <div class="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-100 basis-[3.25rem] flex items-center mr-2">
            <button @click="mobileBack" type="button" class="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
              <span class="sr-only">Open sidebar</span>
              <div class="icon-arrow h-6 w-6 text-gray-400">
                <svg class="duration-300 transition-all ease-in" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>
              </div>
            </button>
            <div class="text-2xl font-bold text-gray-900">{{ selectedPerson.name }}</div>
          </div>
          <div class="pt-2 flex-1 bg-gray-100 overflow-y-auto">
            <content-pane class="col-span-2 bg-gray-100"
              :personData="selectedPerson" 
              :treeData="treeData"
            ></content-pane>

            <!-- Contains select button -->
            <div class="pb-4 basis-14 flex items-center justify-center">
              <button @click="associatePerson" type="button" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">Select</button>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- DESKTOP -->
    <div class="hidden sm:flex flex-col h-full">
      <div class="p-2 flex flew-row flex-wrap basis-[5.25rem] border-b border-gray-200 gap-x-2">
        <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
          <label for="name" class="block text-sm font-medium text-gray-700">Name</label>
          <div class="mt-1">
            <input v-model="filterData.name" type="name" name="name" id="name" class="px-3 py-2 shadow-sm focus:ring-indigo-500 border focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="John Smith">
          </div>
        </div>
        <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
          <label for="country" class="block text-sm font-medium text-gray-700">Country</label>
          <select v-model="filterData.country_id" id="country" name="country" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
            <option></option>
            <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.name }}</option>
          </select>
        </div>
        <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
          <label for="training-center-group" class="block text-sm font-medium text-gray-700">Training Center Group</label>
          <select v-model="filterData.training_center_group_id" id="training-center-group" name="training-center-group" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
            <option></option>
            <option v-for="tcg in trainingCenterGroups" :key="tcg.id" :value="tcg.id">{{ tcg.name }}</option>
          </select>
        </div>
        <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
          <label for="person-role" class="block text-sm font-medium text-gray-700">Role</label>
          <select v-model="filterData.person_role" id="person-role" name="person-role" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
            <option></option>
            <option v-for="r in filterOptions.validPersonRoles" :key="r[0]" :value="r[0]">{{ r[1] }}</option>
          </select>
        </div>
        <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
          <label for="person-role-status" class="block text-sm font-medium text-gray-700">Latest Role Status</label>
          <select v-model="filterData.person_role_status" id="person-role-status" name="person-role-status" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
            <option></option>
            <option v-for="r in filterOptions.statusStrings" :key="r[0]" :value="r[0]">{{ r[1] }}</option>
          </select>
        </div>
        <div class="w-full sm:w-1/3 sm:px-1 mt-1 md:w-1/5">
          <label for="project" class="block text-sm font-medium text-gray-700">Project</label>
          <select v-model="filterData.project_id" id="project" name="project" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
            <option></option>
            <option v-for="project in projects" :key="project.id" :value="project.id">{{ project.name }}</option>
          </select>
        </div>
      </div>

      <div class="pt-2 flex-1 min-h-0 grid grid-cols-3">
        <div class="col-span-1 flex flex-col border-r border-gray-200 overflow-hidden">

          <!-- Scrolling container for person selectors -->
          <div class="flex-1 py-2 overflow-auto">
            <div v-if="noFilter" class="text-base text-center text-gray-500 mt-5">No filter provided.</div>
            
            <div v-if="!noFilter && peopleList">
              <person-select
                :person="person"
                :selectedPerson="selectedPerson"
                @selected="selectPerson"
                v-for="person in peopleList"
                :key="person.person_id"
              ></person-select>
              <InfiniteLoading :identifier="infiniteKey" @infinite="queryPeople"/>
            </div>
          </div>

          <!-- Contains select button -->
          <div class="basis-14 flex items-center justify-center border-t border-gray-200">
            <button @click="associatePerson" type="button" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">Select</button>
          </div>
        </div>

        <content-pane class="col-span-2 bg-gray-100 overflow-y-auto"
          :personData="selectedPerson" 
          :treeData="treeData"
        ></content-pane>
      </div>
    </div>
  </div>
</template>

<script setup>
  import InfiniteLoading from "v3-infinite-loading"
  import "v3-infinite-loading/lib/style.css"
  import { onMounted, ref, computed, watch } from 'vue'

  import { compact, debounce } from 'lodash-es'
  import ContentPane from '@components/person_linkage_system/ContentPane'
  import PersonSelect from '@components/person_linkage_system/PersonSelect'
  import Person from '@models/Person'
  import Country from '@models/Country'
  import SerializedResponse from '@services/SerializedResponse'
  import axios from 'axios'

  const props = defineProps({
    existingPerson: Object,
  })

  const emit = defineEmits(['personToAssociate'])

  let page = ref(1) // Page we are on
  let peopleList = ref([]) // List of people loaded in sidebar
  let infiniteKey = ref(0) // Unique key for infinite component; changes when filter changes
  let loading = ref(false) // Loading state
  let filterHidden = ref(false)
  let filterOuterHidden = ref(false)
  let personSelectorToggle = ref(true)
  let shadeClickCount = ref(0)

  // refs for toggling mobile elements
  let shade = ref(null)
  let recordCount = ref(null)
  let mobileFilter = ref(null)

  let filterData = ref({
    name: null,
    country_id: null,
    project_id: null,
    person_role_status: null,
    role: null,
    training_center_group_id: null,
  })

  let personDetailsResponse = ref(null)

  let filterOptions = ref({
    countriesResponse: null,
    trainingCenterGroupsResponse: null,
    projectsResponse: null,
    validPersonRoles: [
      ['paul', 'Trainer'],
      ['timothy', 'Church Planter'],
      ['titus', 'Group Leader'],
      ['field_rep', 'Coordinator'],
      ['field_rep_manage', 'Coordinator Manage'],
    ],
    statusStrings: [
      ['active', 'Active'],
      ['inactive', 'Inactive'],
      ['dropped', 'Dropped'],
      ['graduated', 'Graduated'],
      ['under_review', 'Under Review'],
      ['suspended', 'Suspended'],
    ],
  })

  let personToAssociate = ref(null)
  let mobileState = ref('person-selector')
  let hideNumResults = ref(false)

  onMounted(() => {
    requestFilterData()
  })

  const selectedPerson = computed(() => {
    return personDetailsResponse.value ? personDetailsResponse.value.modelData : null
  })

  const treeData = computed(() => {
    // return window.gon.generational_tree[0] // implement correctly....get when clicking selected person and pass to content pane
    return {}
  })

  const countries = computed(() => {
    return filterOptions.value.countriesResponse ? filterOptions.value.countriesResponse.modelData : []
  })

  const projects = computed(() => {
    return filterOptions.value.projectsResponse || []
  })

  const trainingCenterGroups = computed(() => {
    return filterOptions.value.trainingCenterGroupsResponse || []
  })

  const noFilter = computed(() => {
    return compact(Object.keys(filterData.value).map(key => filterData.value[key])).length === 0
  })

  const showPersonSelector = computed(() => {
    return mobileState.value === 'person-selector'
  })

  const showContentPane = computed(() => {
    return mobileState.value === 'content-pane'
  })

  watch(filterData, (newVal, oldVal) => {
    requestFilterData(newVal.country_id)

    page.value = 1
    peopleList.value = []
    infiniteKey.value += 1
    loading.value = true
  },{ deep: true })


  function queryPeopleDebounced() {
    debounce(queryPeople, 150)
  }

  function requestFilterData(country_id = '') {
    loading.value = true
    let promises = [
      axios.get('/countries.json').then((resp) => { filterOptions.value.countriesResponse = new SerializedResponse(Country, resp.data); console.log('respy', resp) }),
      axios.get('/projects.json', {
        params: {
          historical: true
        }
      }).then((resp) => { filterOptions.value.projectsResponse = resp.data }),
      
      fetchTrainingCenterGroups(country_id)
    ]

    return Promise.all(promises)
      .then(ps => { loading.value = false })
  }

  function fetchTrainingCenterGroups(country_id) {
    console.log("country_id", country_id)
    return axios.get('/training_center_groups.json', { params: { country_id: country_id } }).then((resp) => { filterOptions.value.trainingCenterGroupsResponse = resp.data })
  }

  function queryPeople($state) {
    let payload = {
      page: page.value,
      ...filterData.value.name && { search: filterData.value.name },
      ...filterData.value.country_id && { country_id: filterData.value.country_id },
      ...filterData.value.training_center_group_id && { training_center_group_id: filterData.value.training_center_group_id },
      ...filterData.value.person_role && { person_role: filterData.value.person_role },
      ...filterData.value.person_role_status && { person_role_status: filterData.value.person_role_status },
      ...filterData.value.project_id && { project_id: filterData.value.project_id },
    }

    console.log("PAYLOAD!", payload)

    if (Object.keys(payload).length === 0) {
      return Promise.resolve([])
    }

    return axios.get('/people/advanced_lookup.json', {
      params: payload,
    }).then(ps => {

      loading.value = false
      peopleList.value.push(...ps.data.records)
      page.value += 1
      
      if (ps.data.records.length === 0) {
        // We loaded the last page; don't request anymore
        // $state && $state.complete()
        $state.complete()
      } else {
        // We finished loading this page, but there is more. Request
        // the next when we scroll down.
        // $state && $state.loaded()
        $state.loaded()
      }
    })
  }

  function selectPerson (person) {
    return axios.get(`/people/${person.person_id}/deep.json`)
      .then(ps => {
        personDetailsResponse.value = new SerializedResponse(Person, ps.data)
        personToAssociate.value = personDetailsResponse.value.modelData
        mobileState.value = 'content-pane'
      })
  }

  function associatePerson () {
    emit('personToAssociate', personToAssociate.value)
  }

  function mobileBack () {
    mobileState.value = 'person-selector'
  }

  function toggleFilter () {
    filterHidden.value = !filterHidden.value
    toggleNumResults()

    if (mobileFilter.value.classList.contains('max-h-0')) {
      mobileFilter.value.classList.remove('max-h-0')
      mobileFilter.value.classList.add('max-h-full')
      mobileFilter.value.classList.add('pb-4')

      shade.value.classList.remove('hidden')
    } else {
      mobileFilter.value.classList.remove('max-h-full')
      mobileFilter.value.classList.add('max-h-0')
      mobileFilter.value.classList.remove('pb-4')

      shade.value.classList.add('hidden')
    }
  }

  function toggleWholeFilter () {
    filterOuterHidden.value = !filterOuterHidden.value
    let filterOuter = document.getElementById("filter-outer")
    filterOuterHidden.value ? filterOuter.classList.add("hidden") : filterOuter.classList.remove("hidden")
  }

  function togglePersonSelect () {
    personSelectorToggle.value = !personSelectorToggle.value
    let personSelect = document.getElementById('person-select')
    personSelectorToggle.value ? personSelect.classList.remove("hidden") : personSelect.classList.add("hidden")
  }

  function toggleShade () {
    shadeClickCount.value++

    if (shadeClickCount.value == 2) {
      toggleFilter()
      shadeClickCount.value = 0
    }
  }

  function toggleNumResults () {
    hideNumResults.value = !hideNumResults.value
  }
</script>

<style lang="scss">
  .slide-enter-active,
  .slide-leave-active {
    @apply transition ease-in-out duration-300 transform;

  }

  .slide-enter,
  .slide-leave-to {
    @apply -translate-x-full;
  }

  .slide-enter-to,
  .slide-leave {
    @apply translate-x-0;
  }


  .fade-enter-active,
  .fade-leave-active {
    @apply transition-opacity ease-in-out duration-300;

  }

  .fade-enter-from {
    @apply opacity-0;
  }

  .fade-leave-to {
    @apply opacity-0;
  }

  .fade-enter-to,
  .fade-leave-from {
    @apply opacity-100;
  }
</style>
